import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { AuthShell, PrimaryButton, AUTH_SUBTLE, AUTH_LABEL, AUTH_BLUE } from './authShared';

const ICON = '#98A2B3';

export default function AdminLogin({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiService.adminLogin(email, password);
      if (response.data.success) {
        const userData = response.data.user;
        onLogin(userData);
        toast.success('Welcome back, Admin!');
        navigate('/admin');
      } else {
        toast.error(response.data.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Login failed. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#EEF2FB' }}>
          <Shield className="w-6 h-6" style={{ color: AUTH_BLUE }} />
        </div>
        <h1 className="text-2xl">Admin Login</h1>
        <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>Restricted access for DrawReady administrators</p>
      </div>

      <form onSubmit={handleAdminLogin} className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium" style={{ color: AUTH_LABEL }}>Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
            <Input type="email" placeholder="admin@drawready.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12" required />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium" style={{ color: AUTH_LABEL }}>Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
            <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10 h-12" required />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70" style={{ color: ICON }}>
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <PrimaryButton type="submit" disabled={isLoading} className="flex items-center justify-center gap-2">
          {isLoading ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Authenticating...</>
          ) : (
            <><Shield className="w-4 h-4" /> Sign In as Admin</>
          )}
        </PrimaryButton>
      </form>

      <div className="pt-4 mt-4 border-t" style={{ borderColor: '#EAECF0' }}>
        <p className="text-xs text-center" style={{ color: AUTH_SUBTLE }}>
          This portal is restricted to authorized administrators only.<br />
          Unauthorized access attempts are logged.
        </p>
      </div>
    </AuthShell>
  );
}
